import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { useCallback } from 'react'
import { ContentItemViewNodesType } from '../../../../types/contentItemViewNode.type'
import { useShallowSelector } from '../../../../Workspace2Store'
import { useProcessSuggestionCards } from '../useProcessSuggestionCards'
import { useTrackChangeCards } from '../useTrackChangeCards'

/**
 * Custom hook to update content nodes in the workspace editor.
 * It handles the state updates for content item view nodes and tree data,
 * and processes suggestion cards if necessary.
 *
 * @returns {(nodes?: Partial<ContentItemViewNodesType>[], shouldReset?: boolean, shouldSetIsSuggestionsDataUpdated?: boolean) => Promise<void>} A function to update the tree state with new nodes.
 */
export const useContentNodeUpdater = () => {
    const { setContentItemViewNodes, setTreeData } = useShallowSelector(
        (s) => ({
            setContentItemViewNodes: s.actions.setContentItemViewNodes,
            setTreeData: s.actions.setTreeData,
        })
    )
    const { setIsSuggestionsDataUpdated } = useSuggestionsShallowSelector(
        (state) => ({
            setIsSuggestionsDataUpdated: state.setIsSuggestionsDataUpdated,
        })
    )
    const { isNotDefaultSuggestionCardFilter } = useTrackChangeCards()
    const { processSuggestionCards } = useProcessSuggestionCards()

    // Optimized tree state update
    const updateTreeState = useCallback(
        async (
            nodes?: Partial<ContentItemViewNodesType>[],
            shouldReset?: boolean,
            shouldSetIsSuggestionsDataUpdated?: boolean
        ) => {
            // Make a backup of the nodes to update
            const backupContentItemViewNodes = [...(nodes || [])]

            // Perform tree update
            if (shouldReset) {
                setContentItemViewNodes([])
                setTreeData([])
            }

            setContentItemViewNodes(backupContentItemViewNodes)

            if (shouldSetIsSuggestionsDataUpdated) {
                // Process suggestion cards
                if (!isNotDefaultSuggestionCardFilter) {
                    processSuggestionCards(backupContentItemViewNodes)
                }
                setIsSuggestionsDataUpdated(shouldSetIsSuggestionsDataUpdated)
            } else {
                setIsSuggestionsDataUpdated(false)
            }
        },
        [
            setTreeData,
            setContentItemViewNodes,
            setIsSuggestionsDataUpdated,
            processSuggestionCards,
            isNotDefaultSuggestionCardFilter,
        ]
    )

    return updateTreeState
}
